import { parseRawData as parseNmeaData } from "./nmeaParser";
import { parseRawData as parseGpxData } from "./gpxParser";
import mapHelper from "@mrwa/rime-replacement/infrastructure/mapHelper";
import requestEsriModules from "@mrwa/rime-replacement/infrastructure/esriModules";

export const bufferDistanceMetres = 50;

/**
 * parses the incoming gps data for the lat/long points and generates a buffered line that encompasses the route
 */
async function calculateBufferedLine(dataRows) {
  const { GeometryUtils, GeometryEngine } = await requestEsriModules({
    GeometryUtils: "esri/geometry/support/jsonUtils",
    GeometryEngine: "esri/geometry/geometryEngineAsync",
  });

  const pathGeo = dataRows
    .map(
      (row) =>
        !(isNaN(row.longitude) || isNaN(row.latitude)) && [
          row.longitude,
          row.latitude,
        ]
    )
    .filter((point) => point);
  const line = await mapHelper.projectToView(
    GeometryUtils.fromJSON({
      paths: [pathGeo],
    })
  );
  //50 metres allows for the gps data to not align perfectly with the road -
  //could potentially be made smaller if too many close roads are being picked up and performance becomes an issue
  return await GeometryEngine.buffer(line, bufferDistanceMetres, "meters");
}

async function gpsToPoint(gpsRow) {
  const { Point } = await requestEsriModules({
    Point: "esri/geometry/Point",
  });

  const { latitude, longitude } = gpsRow;
  return await mapHelper.projectToView(
    new Point({
      latitude,
      longitude,
      spatialReference: { wkid: 4326 }, //4326 is WGS84, spatial reference used by GPS systems
    })
  );
}

export function parseGpsData(data, fileType, includeGeometry = false) {
  switch (fileType) {
    case "NMEA":
    case "LOG":
      return parseNmeaData(data).map((gpsRow) => ({
        ...gpsRow,
        projectedGeometry: includeGeometry && gpsToPoint(gpsRow),
      }));
    case "GPX":
      return parseGpxData(data).map((gpsRow) => ({
        ...gpsRow,
        projectedGeometry: includeGeometry && gpsToPoint(gpsRow),
      }));
    default:
      throw new Error(`Unknown GPS file type: ${fileType}`);
  }
}

export default class gpsData {
  #dataRows;
  #bufferedLine;
  #offsetSeconds;

  get bufferedGeometry() {
    return this.#bufferedLine;
  }

  readGpsFile(fileUnion, onReadComplete) {
    this.#dataRows = null;
    fileUnion.case({
      FileSystem: (file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const data = reader.result;
          const fileType =
            file.type.toUpperCase() ||
            file.name.substring(file.name.lastIndexOf(".") + 1).toUpperCase();

          this.#dataRows = parseGpsData(data, fileType, true);
          this.#bufferedLine = calculateBufferedLine(this.#dataRows);

          onReadComplete &&
            onReadComplete(this.#dataRows, this.bufferedGeometry);
        };

        reader.readAsText(file);
      },
      Url: async (url, name) => {
        const gpsDataResponse = await fetch(url);
        const data = await gpsDataResponse.text();
        this.#dataRows = parseGpsData(
          data,
          name.substring(name.lastIndexOf(".") + 1).toUpperCase(),
          true
        );
        this.#bufferedLine = calculateBufferedLine(this.#dataRows);

        onReadComplete && onReadComplete(this.#dataRows, this.bufferedGeometry);
      },
    });
  }

  setTimeOffset(offsetSeconds) {
    this.#offsetSeconds = offsetSeconds;
  }

  /**
   * given a specified time the function will return the data of the nearest point before that time in the timeline
   */
  getDataAtTime(time) {
    if (!this.#dataRows) return null;

    //creating a timeline in milliseconds
    //have to start timeline at 1970 as some rows don't have dates set
    const timeline = this.#dataRows.map((data) =>
      data.dateSet
        ? data.dateTime.getTime()
        : Date.parse("1970-01-01" + data.time)
    );
    const firstTime = timeline[0];
    const gpsDesiredTime =
      firstTime + (time + (this.#offsetSeconds ?? 0)) * 1000;
    const cutTimeline = timeline.filter((time) => time <= gpsDesiredTime);

    const result = this.#dataRows[cutTimeline.length - 1];
    return {
      ...result,
      isValidGeometry:
        result && !isNaN(result.latitude) && !isNaN(result.longitude),
    };
  }
}
